/**
 *  Constains data used for UI building; e.g. labels and their
 *  corresponding ids
 */
export type PageNamesType = keyof typeof config.PAGE_NAMES;

const config = {
  LANDING_PAGES: {
    DEFAULT: 'myymassa',
    HOME_INSURANCE: 'kotivakuutus',
    HOW_WE_SELL: 'asunnon-myynti',
    BUYING: 'ostamassa',
    SELLING: 'myymassa',
    RENTING: 'vuokraamassa',
    SERVICES: 'kodin-palvelut',
    AGENCIES: 'kiinteistonvalitys',
    LOAN_PAGE: 'asuntolaina',
    CONTACT_PAGE: 'ota-yhteytta',
    SELLER_SERVICE: 'myyjan-palvelu',
    DIAS: 'digitaalinen-asuntokauppa',
    STYLING_SERVICES: 'stailauspalvelut',
    CAREERS: 'avoimet-tyopaikat',
    CAREERS_AGENT: 'avoimet-tyopaikat-valittaja',
    CHANGE_APARTMENTS: 'asunnonvaihto',
    APARTMENT_PRICE: 'asunnon-myyntihinta',
    SALES_AND_MARKETING: 'asunnon-markkinointi',
    BUYING_REQUEST_TASK: 'ostotoimeksianto',
    MOVING_FORM_LANDING_PAGE: 'muuttoilmoitus',
    RESIDENCES_PAGE: 'asukassivut',
    OFFER_LANDING_PAGE: 'jata-tarjouspyynto',
    RENTING_SECTION_PAGE: 'vuokraajalle',
    SELL_NEW_APARTMENTS: 'uudiskohteiden-myynti',
    SELLING_YOUR_COTTAGE: 'mokin-myynti',
    RESPONSIBILITY_PROGRAM: 'vastuullisuus',
    SHOWING_WEEKEND: 'esittelyviikonloppu',
    EVALUATION_SPECIALIST: 'arviointiasiantuntija',
  },
  SCROLL_SECTIONS: ['analytiikka', 'contact'],
  COMMISSION_TYPES: {
    Property: 'K',
    Share: 'O',
    Rent: 'V',
    Business: 'T',
  },
  LISTING_TYPES: {
    89: {
      name: 'Kerrostalo',
      primaryArea: 'livingArea',
    },
    90: {
      name: 'Omakotitalo',
      primaryArea: 'livingArea',
    },
    91: {
      name: 'Rivitalo',
      primaryArea: 'livingArea',
    },
    92: {
      name: 'Paritalo',
      primaryArea: 'livingArea',
    },
    93: {
      name: 'Erillistalo',
      primaryArea: 'livingArea',
    },
    112: {
      name: 'Puutalo',
      primaryArea: 'livingArea',
    },
    113: {
      name: 'Luhtitalo',
      primaryArea: 'livingArea',
    },
    470: {
      name: 'Kytketty paritalo',
      primaryArea: 'livingArea',
    },
    96: {
      name: 'Vapaa-ajan asunto',
      primaryArea: 'livingArea',
    },
    471: {
      name: 'Mökki tai huvila',
      primaryArea: 'livingArea',
    },
    115: {
      name: 'Toimistotila',
      primaryArea: 'totalArea',
    },
    116: {
      name: 'Liiketila',
      primaryArea: 'totalArea',
    },
    117: {
      name: 'Tuotantotila',
      primaryArea: 'totalArea',
    },
    118: {
      name: 'Muu toimitila',
      primaryArea: 'totalArea',
    },
    105: {
      name: 'Metsätila',
      primaryArea: 'siteArea',
    },
    106: {
      name: 'Maatila',
      primaryArea: 'siteArea',
    },
    109: {
      name: 'Peltotila',
      primaryArea: 'siteArea',
    },
    108: {
      name: 'Tontti',
      primaryArea: 'siteArea',
    },
    104: {
      name: 'Rt-tontti',
      primaryArea: 'siteArea',
    },
    95: {
      name: 'Okt-tontti',
      primaryArea: 'siteArea',
    },
    107: {
      name: 'Vapaa-ajan tontti',
      primaryArea: 'siteArea',
    },
    94: {
      name: 'Autotalli',
      primaryArea: 'totalArea',
    },
    472: {
      name: 'Autohalli',
      primaryArea: 'totalArea',
    },
    473: {
      name: 'Autokatospaikka',
      primaryArea: 'totalArea',
    },
    110: {
      name: 'Autopaikka',
      primaryArea: 'totalArea',
    },
    111: {
      name: 'Varastotila',
      primaryArea: 'totalArea',
    },
    114: {
      name: 'Muu kohde',
      primaryArea: 'totalArea',
    },
  },
  PAGE_NAMES: {
    LANDING: 'landing',
    LISTING: 'listing',
    AGENCY: 'agency',
    COMPANY_AGENCY: 'companyAgency',
    AGENT: 'agent',
    LOGIN: 'login',
    CURRENT_NEWS: 'currentNews',
    CAREER_STORIES: 'careerStories',
  },
  APARTMENT_CONDITIONS: {
    119: 'Uusi',
    120: 'Hyvä',
    121: 'Tyydyttävä',
    122: 'Välttävä',
  },
  LANGUAGES: {
    en: 'englanti',
    sv: 'ruotsi',
    fi: 'suomi',
    ru: 'venäjä',
    de: 'saksa',
    fr: 'ranska',
    es: 'espanja',
  },
  weekdays: ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la'],
  TOP_AGENCIES: [
    { name: 'Helsinki', shortUrl: 'helsinki' },
    { name: 'Lappeenranta', shortUrl: 'lappeenranta' },
    { name: 'Tampere', shortUrl: 'tampere' },
    { name: 'Turku', shortUrl: 'turku' },
    { name: 'Jyväskylä', shortUrl: 'jyvaskyla' },
    { name: 'Hämeenlinna', shortUrl: 'hameenlinna' },
    { name: 'Kuopio', shortUrl: 'kuopio' },
    { name: 'Oulu', shortUrl: 'oulu' },
    { name: 'Pori', shortUrl: 'pori' },
    { name: 'Rovaniemi', shortUrl: 'rovaniemi' },
    { name: 'Kouvola', shortUrl: 'kouvola' },
    { name: 'Kajaani', shortUrl: 'kajaani' },
    { name: 'Joensuu', shortUrl: 'joensuu' },
    { name: 'Mikkeli', shortUrl: 'mikkeli' },
    { name: 'Vaasa', shortUrl: 'vaasa' },
    { name: 'Seinäjoki', shortUrl: 'seinajoki' },
    { name: 'Kokkola', shortUrl: 'kokkola' },
    { name: 'Vantaa', shortUrl: 'vantaa' },
    { name: 'Rauma', shortUrl: 'rauma' },
    { name: 'Espoo', shortUrl: 'espoo' },
    { name: 'Lahti', shortUrl: 'lahti' },
    { name: 'Kotka', shortUrl: 'kotka' },
  ],
  TOP_SERVICES: [
    {
      name: 'Nuohous',
      locations: ['Oulu', 'Helsinki', 'Vantaa', 'Espoo', 'Porvoo', 'Joensuu'],
    },
    {
      name: 'Sähkötyöt',
      locations: ['Helsinki', 'Oulu', 'Turku', 'Tampere', 'Pori', 'Espoo'],
    },
    {
      name: 'Sisäilmatutkimus',
      locations: ['Helsinki', 'Espoo', 'Tampere', 'Oulu', 'Turku', 'Jyväskylä'],
    },
    {
      name: 'Remontointi',
      locations: ['Helsinki', 'Turku', 'Espoo', 'Kuopio'],
    },
  ],
  ARTICLE_TAGS: ['Asuntosijoittaminen', 'Ensiasunto', 'Oma koti', 'Vapaa-ajan asunto'],
  ACCEPTED_INPUT_TYPES: ['tel', 'email', 'number', 'text', 'password', 'textarea'],
  TEXT_INPUT_DEBOUNCE: 700,
  CONDITION_TYPES: {
    new: 119,
    good: 120,
    decent: 121,
    poor: 122,
  },
  TYPES: {
    apartment_building: 89,
    town_house: 90,
    row_house: 91,
    semi_detached_house: 92,
    detached_house: 93,
    wooden_house: 112,
    luhtitalo: 113,
    connected_semi_detached: 470,
  },
  BUILDING_TYPE_NAMES: {
    APARTMENT_BUILDING: 'Kerrostalo',
    ROW_HOUSE: 'Rivitalo',
    SEMI_DETACHED_HOUSE: 'Paritalo',
    OTHER_BUILDING: 'Muu talo',
  },
};

export default config;
